// ABOUTME: Bottom sheet flow for logging daily outfit wear
// ABOUTME: Lets users select items worn today with optional occasion and rating

import { CheckIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { toast } from 'sonner'
import BottomSheet from '@/components/layout/BottomSheet'
import type { WardrobeItem } from '@/lib/store/wardrobeStore'
import { useWardrobeStore } from '@/lib/store/wardrobeStore'
import { useWearLogStore } from '@/lib/store/wearLogStore'

const CATEGORIES = ['tops', 'bottoms', 'shoes', 'outerwear', 'accessories'] as const

const OCCASIONS = ['work', 'casual', 'date', 'weekend', 'formal', 'active'] as const

interface WearLogFlowProps {
  onClose: () => void
}

export default function WearLogFlow({ onClose }: WearLogFlowProps) {
  const { items } = useWardrobeStore()
  const { logWear } = useWearLogStore()

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [occasion, setOccasion] = useState('')
  const [rating, setRating] = useState<number | undefined>(undefined)

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleSubmit = () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one item')
      return
    }

    const today = new Date().toISOString().split('T')[0]

    logWear({
      date: today,
      itemIds: Array.from(selectedIds),
      source: 'manual',
      occasion: occasion || undefined,
      rating,
    })

    toast.success('Outfit logged!')
    onClose()
  }

  const itemsByCategory = CATEGORIES.reduce(
    (acc, cat) => {
      const catItems = items.filter((i) => i.category === cat)
      if (catItems.length > 0) acc[cat] = catItems
      return acc
    },
    {} as Record<string, WardrobeItem[]>
  )

  return (
    <BottomSheet open onClose={onClose} title="Log Today's Outfit" size="full">
      <div className="space-y-6">
        {Object.entries(itemsByCategory).map(([category, catItems]) => (
          <div key={category}>
            <h3 className="text-sm font-medium text-fg-secondary capitalize mb-2">{category}</h3>
            <div className="grid grid-cols-3 gap-2">
              {catItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggleItem(item.id)}
                  className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                    selectedIds.has(item.id)
                      ? 'border-primary-500 ring-1 ring-primary-500'
                      : 'border-transparent'
                  }`}
                >
                  <img
                    src={item.thumbnailData}
                    alt={item.name}
                    className="w-full aspect-square object-cover"
                  />
                  {selectedIds.has(item.id) && (
                    <div className="absolute top-1 right-1 bg-primary-500 rounded-full p-0.5">
                      <CheckIcon className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <p className="text-xs text-fg-primary truncate px-1 py-0.5">{item.name}</p>
                </button>
              ))}
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <p className="text-center text-fg-secondary py-8">Add items to your wardrobe first</p>
        )}

        <div>
          <label
            htmlFor="occasion-select"
            className="text-sm font-medium text-fg-secondary mb-1 block"
          >
            Occasion (optional)
          </label>
          <select
            id="occasion-select"
            value={occasion}
            onChange={(e) => setOccasion(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-surface-elevated border border-fg-tertiary/20 text-fg-primary text-sm"
          >
            <option value="">No occasion</option>
            {OCCASIONS.map((occ) => (
              <option key={occ} value={occ}>
                {occ.charAt(0).toUpperCase() + occ.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <p className="text-sm font-medium text-fg-secondary mb-1">How did it feel? (optional)</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(rating === n ? undefined : n)}
                className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                  rating === n
                    ? 'bg-primary-500 text-white'
                    : 'bg-surface text-fg-secondary hover:bg-surface-elevated'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={selectedIds.size === 0}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Log Outfit ({selectedIds.size} items)
        </button>
      </div>
    </BottomSheet>
  )
}
